import type { MouseEventHandler, ReactNode } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { parseExtraGuestGeneratedPolicies } from 'components/AccommodationKit/AccommodationAmenitiesAndPoliciesContent'
import ModalContext from 'contexts/ModalContext'
import { useInlineListFormatter } from 'hooks/useListFormatters'
import PackageInclusionsPolicyModal from './PackageInclusionsPolicyModal'

const ValidityText = styled(BodyText)`
  margin-top: ${rem(8)};
`

type Props = {
  includedGuests: Array<App.PackageRoomIncludedGuests>
  policyDescription?: string
  selectedOccupancy?: App.Occupants
}

export function hasIncludedGuestCombinations(includedGuests: Array<App.PackageRoomIncludedGuests>): boolean {
  return includedGuests.some(includedGuest => (
    includedGuest.adults > 0 ||
    includedGuest.teenagers > 0 ||
    includedGuest.children > 0 ||
    includedGuest.infants > 0
  ))
}

function getGeneratedPackageInclusions(policyDescription?: string): Array<string> {
  return parseExtraGuestGeneratedPolicies(policyDescription)?.packageInclusions.filter(Boolean) ?? []
}

export function hasPackageInclusionsValidityText(includedGuests: Array<App.PackageRoomIncludedGuests>, policyDescription?: string): boolean {
  return getGeneratedPackageInclusions(policyDescription).length > 0 || hasIncludedGuestCombinations(includedGuests)
}

function getTotalGuests({ adults, teenagers, children, infants }: App.PackageRoomIncludedGuests): number {
  return adults + teenagers + children + infants
}

function hasEveryGuestSplitForTotal(includedGuests: Array<App.PackageRoomIncludedGuests>): boolean {
  if (!includedGuests.length) return false

  const maxGuests = Math.max(...includedGuests.map(getTotalGuests))

  if (includedGuests.some(includedGuest => getTotalGuests(includedGuest) !== maxGuests)) {
    return false
  }

  const activeExtraGuestTypes = (['teenagers', 'children', 'infants'] as const).filter(guestType =>
    includedGuests.some(includedGuest => includedGuest[guestType] > 0),
  )

  if (!activeExtraGuestTypes.length) {
    return false
  }

  const buildKey = (adults: number, extrasByType: Partial<Record<(typeof activeExtraGuestTypes)[number], number>>) => [
    adults,
    activeExtraGuestTypes.includes('teenagers') ? extrasByType.teenagers ?? 0 : undefined,
    activeExtraGuestTypes.includes('children') ? extrasByType.children ?? 0 : undefined,
    activeExtraGuestTypes.includes('infants') ? extrasByType.infants ?? 0 : undefined,
  ].filter(value => value !== undefined).join('|')

  const actualCombos = new Set(includedGuests.map(({ adults, teenagers, children, infants }) => buildKey(adults, { teenagers, children, infants })))

  if (actualCombos.size !== includedGuests.length) {
    return false
  }

  const expectedCombos = new Set<string>()

  if (activeExtraGuestTypes.length === 1) {
    const [type] = activeExtraGuestTypes
    for (let adults = 1; adults <= maxGuests; adults++) {
      expectedCombos.add(buildKey(adults, { [type]: maxGuests - adults }))
    }
  } else if (activeExtraGuestTypes.length === 2) {
    const [typeA, typeB] = activeExtraGuestTypes
    for (let adults = 1; adults <= maxGuests; adults++) {
      const remaining = maxGuests - adults
      for (let a = 0; a <= remaining; a++) {
        expectedCombos.add(buildKey(adults, { [typeA]: a, [typeB]: remaining - a }))
      }
    }
  } else {
    for (let adults = 1; adults <= maxGuests; adults++) {
      const remaining = maxGuests - adults
      for (let teenagers = 0; teenagers <= remaining; teenagers++) {
        for (let children = 0; children <= remaining - teenagers; children++) {
          expectedCombos.add(buildKey(adults, {
            teenagers,
            children,
            infants: remaining - teenagers - children,
          }))
        }
      }
    }
  }

  if (actualCombos.size !== expectedCombos.size) {
    return false
  }

  for (const combo of expectedCombos) {
    if (!actualCombos.has(combo)) {
      return false
    }
  }

  return true
}

function formatCollapsedGuestCombination(intl: ReturnType<typeof useIntl>, includedGuests: Array<App.PackageRoomIncludedGuests>): string | undefined {
  if (!hasEveryGuestSplitForTotal(includedGuests)) {
    return undefined
  }

  const count = getTotalGuests(includedGuests[0])

  return intl.formatMessage(
    {
      id: '0pm7thejFZlprcYOBHbi',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Collapsed package inclusions validity guest count when every split of the same total guest count receives inclusions',
    },
    { count },
  )
}

function formatIncludedGuestCombination(intl: ReturnType<typeof useIntl>, includedGuests: App.PackageRoomIncludedGuests): string {
  const parts = [
    includedGuests.adults > 0 ? intl.formatMessage(
      {
        id: 'MauzMmZvkQuvuh11L00p',
        defaultMessage: '{count, plural, one {# adult} other {# adults}}',
        description: 'Adult guest count included in the package inclusions validity note',
      },
      { count: includedGuests.adults },
    ) : '',
    includedGuests.teenagers > 0 ? intl.formatMessage(
      {
        id: 'alkqCuYpoGP0UBwjqurw',
        defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}',
        description: 'Teenage guest count included in the package inclusions validity note',
      },
      { count: includedGuests.teenagers },
    ) : '',
    includedGuests.children > 0 ? intl.formatMessage(
      {
        id: 'LD0dqRyj_xansDw80LtZ',
        defaultMessage: '{count, plural, one {# child} other {# children}}',
        description: 'Child guest count included in the package inclusions validity note',
      },
      { count: includedGuests.children },
    ) : '',
    includedGuests.infants > 0 ? intl.formatMessage(
      {
        id: 'Sm31_-fCutJQJfQh3gVo',
        defaultMessage: '{count, plural, one {# infant} other {# infants}}',
        description: 'Infant guest count included in the package inclusions validity note',
      },
      { count: includedGuests.infants },
    ) : '',
  ].filter(Boolean)

  return parts.join(', ')
}

function getMatchingGeneratedPackageInclusion(
  intl: ReturnType<typeof useIntl>,
  generatedPackageInclusions: Array<string>,
  selectedOccupancy?: App.Occupants,
): string | undefined {
  if (!selectedOccupancy) return undefined

  const selectedOccupancyText = formatIncludedGuestCombination(intl, {
    adults: selectedOccupancy.adults,
    teenagers: selectedOccupancy.teenagers ?? 0,
    children: selectedOccupancy.children ?? 0,
    infants: selectedOccupancy.infants ?? 0,
  })

  return generatedPackageInclusions.find(packageInclusion => packageInclusion === selectedOccupancyText)
}

function getIncludedGuestCombinations(
  intl: ReturnType<typeof useIntl>,
  includedGuests: Array<App.PackageRoomIncludedGuests>,
  policyDescription?: string,
  selectedOccupancy?: App.Occupants,
): Array<string> {
  const generatedPackageInclusions = getGeneratedPackageInclusions(policyDescription)

  if (generatedPackageInclusions.length > 0) {
    const matchingGeneratedPackageInclusion = getMatchingGeneratedPackageInclusion(intl, generatedPackageInclusions, selectedOccupancy)

    if (matchingGeneratedPackageInclusion) {
      return [matchingGeneratedPackageInclusion]
    }

    return generatedPackageInclusions
  }

  const collapsedCombination = formatCollapsedGuestCombination(intl, includedGuests)

  if (collapsedCombination) {
    return [collapsedCombination]
  }

  return Array.from(new Set(includedGuests.map(includedGuest => formatIncludedGuestCombination(intl, includedGuest)).filter(Boolean)))
}

function PackageInclusionsValidityText({ includedGuests, policyDescription, selectedOccupancy }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const formatDisjunctionList = useInlineListFormatter('disjunction')

  const includedGuestCombinations = useMemo(() => getIncludedGuestCombinations(intl, includedGuests, policyDescription, selectedOccupancy), [includedGuests, intl, policyDescription, selectedOccupancy])
  const validOccupancies = useMemo(() => formatDisjunctionList(includedGuestCombinations), [formatDisjunctionList, includedGuestCombinations])
  const onViewPolicyClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()

    if (policyDescription) {
      showModal(<PackageInclusionsPolicyModal policyDescription={policyDescription} />)
    }
  }, [policyDescription, showModal])

  return (
    <ValidityText variant="small" colour="neutral-three">
      {policyDescription && intl.formatMessage(
        {
          id: 'X05WkOlMVILmoKWfgCYz',
          defaultMessage: 'Inclusions valid for {validOccupancies}. Extra guest inclusions may vary. <link>View policy</link>',
          description: 'Note shown below an accommodation package explaining which guest combinations receive the package inclusions with a link to policy details',
        },
        {
          validOccupancies,
          link: (chunks: ReactNode) => <TextLink size="small" colour="neutral-three" weight="regular" key="view-policy" onClick={onViewPolicyClick}>{chunks}</TextLink>,
        },
      )}
      {!policyDescription && intl.formatMessage(
        {
          id: 'cgVMOZx4SClEKWt6xesD',
          defaultMessage: 'Inclusions valid for {validOccupancies}. Extra guest inclusions may vary.',
          description: 'Note shown below an accommodation package explaining which guest combinations receive the package inclusions',
        },
        { validOccupancies },
      )}
    </ValidityText>
  )
}

export default PackageInclusionsValidityText
